import React, { useState, useMemo, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useToast } from '../../context/ToastContext';
import { useLicense } from '../../context/LicenseContext';
import { useAlarmSocket } from '../../context/AlarmSocket';

const SITES = ['Town Hall', 'Civic Centre', 'Aquatic Centre', 'Council Depot', 'Parramatta Square', 'River Walk'];

// Sparkline helper (simple bar-style micro chart)
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data, 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:'2px', height:'32px' }}>
      {data.map((v, i) => (
        <div key={i} style={{ flex:1, background:`${color}${i===data.length-1?'ff':'55'}`, borderRadius:'2px', height:`${Math.round((v/max)*100)}%`, minHeight:'2px', transition:'height 0.3s' }} />
      ))}
    </div>
  );
}

// Ring progress
function Ring({ pct, color, size = 64 }: { pct: number; color: string; size?: number }) {
  const r = (size/2) - 6;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct / 100;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2+5} textAnchor="middle" fontSize="13" fontWeight="700" fill={color}>{pct}%</text>
    </svg>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const RISK_COLOR: Record<string,string> = { high:'#ef4444', medium:'#f59e0b', low:'#22c55e' };
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// Skeleton row helper
function SkeletonRow({ height = 14, width = '100%', mb = 0 }: { height?: number; width?: string | number; mb?: number }) {
  return <div className="skeleton" style={{ height, width, borderRadius:6, marginBottom:mb }} />;
}
function SkeletonCard({ h = 80 }: { h?: number }) {
  return <div className="skeleton" style={{ height:h, borderRadius:12, width:'100%' }} />;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ExecutiveDashboard() {
  const { toast } = useToast();
  const { isProtegeOrg } = useLicense();
  const { alarms: wsAlarms } = useAlarmSocket();
  const [period, setPeriod] = useState<'today'|'week'|'month'>('week');
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);

  // ── Live data from all APIs ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [armorAlarms, setArmorAlarms] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [healthData, setHealthData] = useState<any>(null);
  const [protegeControllers, setProtegeControllers] = useState<any[]>([]);
  const [protegeEvents, setProtegeEvents] = useState<any[]>([]);

  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    const hdr = { Authorization: `Bearer ${tok}` };
    const fetches: Promise<any>[] = [
      fetch('/api/armorip/alarms', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/jobs', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/health', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null),
    ];
    if (isProtegeOrg) {
      fetches.push(
        fetch('/api/protege/controllers', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/protege/events?limit=200', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null),
      );
    }
    Promise.all(fetches).then(([aRes, jRes, hRes, ctrlRes, evtRes]) => {
      setArmorAlarms(aRes?.alarms || []);
      setJobs(jRes || []);
      setHealthData(hRes || null);
      if (ctrlRes) setProtegeControllers(ctrlRes.controllers || []);
      if (evtRes) setProtegeEvents(evtRes.events || []);
    }).finally(() => setLoading(false));
    // Refresh every 2 minutes
    const interval = setInterval(() => {
      fetch('/api/armorip/alarms', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null).then(aRes => { if (aRes) setArmorAlarms(aRes.alarms || []); });
      fetch('/api/jobs', { headers: hdr }).then(r => r.ok ? r.json() : null).catch(() => null).then(jRes => { if (jRes) setJobs(jRes); });
    }, 120000);
    return () => clearInterval(interval);
  }, [isProtegeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived KPIs ─────────────────────────────────────────────────────────────
  const kpiData = useMemo(() => {
    const activeAlarms = armorAlarms.filter((a: any) => !a.acknowledged && !a.closedAt);
    const criticalCount = activeAlarms.filter((a: any) => a.severity === 'critical').length;

    // Alarm Response SLA: % of acknowledged alarms acked within 4 min (240s)
    const ackedAlarms = armorAlarms.filter((a: any) => a.acknowledged && a.tsMs && a.acknowledgedAt);
    const slaMet = ackedAlarms.filter((a: any) => {
      const ackMs = new Date(a.acknowledgedAt).getTime();
      return (ackMs - a.tsMs) <= 240000;
    }).length;
    const slaPct = ackedAlarms.length > 0 ? Math.round((slaMet / ackedAlarms.length) * 100) : (activeAlarms.length === 0 ? 100 : 0);

    // Camera uptime
    const camTotal = healthData?.cameras?.total ?? 0;
    const camOnline = healthData?.cameras?.online ?? 0;
    const camPct = camTotal > 0 ? Math.round((camOnline / camTotal) * 100) : (healthData ? 100 : 98);

    // Job resolution SLA: % of closed jobs closed within 60 min
    const closedJobs = jobs.filter((j: any) => j.status === 'closed' && j.createdAt && j.closedAt);
    const jobSlaMet = closedJobs.filter((j: any) => {
      const diff = new Date(j.closedAt).getTime() - new Date(j.createdAt).getTime();
      return diff <= 3600000;
    }).length;
    const jobSlaPct = closedJobs.length > 0 ? Math.round((jobSlaMet / closedJobs.length) * 100) : 88;

    return [
      {
        label: 'Alarm Response SLA',
        value: `${slaPct}%`,
        target: '95%',
        pct: slaPct,
        color: slaPct >= 95 ? '#22c55e' : slaPct >= 80 ? '#f59e0b' : '#ef4444',
        trend: ackedAlarms.length > 0 ? `${slaMet}/${ackedAlarms.length} within 4 min` : 'No acked alarms yet',
        sub: `Target: <4 min avg · ${activeAlarms.length} active now`,
      },
      {
        label: 'Camera Uptime',
        value: `${camPct}%`,
        target: '99%',
        pct: camPct,
        color: camPct >= 99 ? '#22c55e' : camPct >= 90 ? '#f59e0b' : '#ef4444',
        trend: camTotal > 0 ? `${camOnline}/${camTotal} online` : 'Health check pending',
        sub: camTotal > 0 ? `${camTotal - camOnline} offline` : 'Connect health monitor',
      },
      {
        label: 'Active Alarms',
        value: String(activeAlarms.length),
        target: '0',
        pct: Math.min(100, activeAlarms.length * 10),
        color: activeAlarms.length === 0 ? '#22c55e' : activeAlarms.length <= 3 ? '#f59e0b' : '#ef4444',
        trend: `${criticalCount} critical`,
        sub: `${armorAlarms.filter((a:any)=>a.closedAt).length} resolved today`,
      },
      {
        label: 'Job Resolution SLA',
        value: `${jobSlaPct}%`,
        target: '90%',
        pct: jobSlaPct,
        color: jobSlaPct >= 90 ? '#22c55e' : jobSlaPct >= 70 ? '#f59e0b' : '#ef4444',
        trend: closedJobs.length > 0 ? `${jobSlaMet}/${closedJobs.length} within 1 hr` : 'No closed jobs yet',
        sub: `${jobs.filter((j:any)=>j.status!=='closed').length} open jobs`,
      },
    ];
  }, [armorAlarms, jobs, healthData]);

  const siteData = useMemo(() => {
    const alarmsBySite: Record<string, number> = {};
    const jobsBySite: Record<string, number> = {};
    armorAlarms.filter((a:any) => !a.closedAt).forEach((a:any) => {
      const s = a.site || 'Unknown';
      alarmsBySite[s] = (alarmsBySite[s] || 0) + 1;
    });
    jobs.filter((j:any) => j.status !== 'closed').forEach((j:any) => {
      const s = j.site || 'Unknown';
      jobsBySite[s] = (jobsBySite[s] || 0) + 1;
    });
    const sites = Array.from(new Set([
      ...Object.keys(alarmsBySite),
      ...Object.keys(jobsBySite),
      ...(protegeControllers.map((c:any) => c.name)),
    ])).filter(s => s !== 'Unknown').slice(0, 8);

    if (sites.length === 0) {
      // fallback representative data
      return [
        { site:'Town Hall',         alarms:0, jobs:0, cameras:'—', status:'Normal', risk:'low' },
        { site:'Civic Centre',      alarms:0, jobs:0, cameras:'—', status:'Normal', risk:'low' },
      ];
    }
    return sites.map(site => {
      const alarmCount = alarmsBySite[site] || 0;
      const jobCount = jobsBySite[site] || 0;
      const risk = alarmCount >= 3 ? 'high' : alarmCount >= 1 ? 'medium' : 'low';
      const camData = healthData?.cameras?.details?.filter((c:any) => (c.site||'').toLowerCase().includes(site.toLowerCase().split(' ')[0]));
      const camStr = camData?.length > 0 ? `${camData.filter((c:any)=>c.reachable).length}/${camData.length}` : '—';
      return { site, alarms: alarmCount, jobs: jobCount, cameras: camStr, status: alarmCount > 0 ? 'Alert' : 'Normal', risk };
    });
  }, [armorAlarms, jobs, protegeControllers, healthData]);

  const criticalData = useMemo(() => {
    return armorAlarms
      .filter((a:any) => !a.closedAt && (a.severity === 'critical' || a.severity === 'high'))
      .slice(0, 5)
      .map((a:any) => ({
        time: (a.timestamp || '').slice(11,16) || '--:--',
        type: a.type || 'Alarm',
        site: a.site || 'Unknown',
        job: a.jobNumber || `ALM-${String(a.id).slice(-4)}`,
        status: a.acknowledged ? 'acknowledged' : 'active',
      }));
  }, [armorAlarms]);

  // Weekly trend — group alarms by day of week
  const weeklyAlarms = useMemo(() => {
    const counts = [0,0,0,0,0,0,0];
    const now = Date.now();
    armorAlarms.forEach((a:any) => {
      const ms = a.tsMs || new Date(a.timestamp || 0).getTime();
      const daysAgo = Math.floor((now - ms) / 86400000);
      if (daysAgo >= 0 && daysAgo < 7) counts[6 - daysAgo]++;
    });
    return counts;
  }, [armorAlarms]);

  const weeklyJobs = useMemo(() => {
    const counts = [0,0,0,0,0,0,0];
    const now = Date.now();
    jobs.forEach((j:any) => {
      const ms = new Date(j.createdAt || j.created_at || 0).getTime();
      const daysAgo = Math.floor((now - ms) / 86400000);
      if (daysAgo >= 0 && daysAgo < 7) counts[6 - daysAgo]++;
    });
    return counts;
  }, [jobs]);

  const topStats = useMemo(() => {
    const active = armorAlarms.filter((a:any) => !a.closedAt);
    const openJobs = jobs.filter((j:any) => j.status !== 'closed');
    const camTotal = healthData?.cameras?.total ?? 0;
    const camOnline = healthData?.cameras?.online ?? camTotal;
    return [
      { l:'Active Alarms',   v:String(active.length),    delta:'live', good:active.length===0, arrow:active.length===0?'✓':'⚠', color:'#ef4444', spark:weeklyAlarms },
      { l:'Open Jobs',       v:String(openJobs.length),  delta:'open', good:openJobs.length<5, arrow:openJobs.length===0?'✓':'↑', color:'#f59e0b', spark:weeklyJobs },
      { l:'Cameras Online',  v:camTotal>0?`${camOnline}/${camTotal}`:'—', delta:'uptime', good:camOnline===camTotal, arrow:camOnline===camTotal?'✓':'⚠', color:'#22c55e', spark:[98,99,97,100,99,98,camTotal>0?Math.round(camOnline/camTotal*100):99] },
      { l:'Sites Monitored', v:String(siteData.length),  delta:'sites', good:true, arrow:'', color:'#3b82f6', spark:[siteData.length,siteData.length,siteData.length,siteData.length,siteData.length,siteData.length,siteData.length] },
    ];
  }, [armorAlarms, jobs, healthData, weeklyAlarms, weeklyJobs, siteData]);

  function handleExport() {
    setExporting(true);
    const w = window.open('', '_blank');
    if (w) {
      const now = new Date().toLocaleString('en-AU', { dateStyle:'full', timeStyle:'short' });
      const activeA = armorAlarms.filter((a:any) => !a.closedAt);
      w.document.write(`<!DOCTYPE html><html><head><title>Executive Report — ${now}</title><style>body{font-family:Arial,sans-serif;padding:32px;color:#0a0a0a}h1{font-size:22px;margin-bottom:4px}h2{font-size:14px;margin:24px 0 10px;border-bottom:2px solid #3b82f6;padding-bottom:6px}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#f1f5f9;padding:8px;text-align:left;border:1px solid #e2e8f0}td{padding:8px;border:1px solid #e2e8f0}.kpi{display:inline-block;margin:8px;padding:14px 20px;border:1px solid #e2e8f0;border-radius:8px;min-width:140px}.kpi-v{font-size:28px;font-weight:800;color:#1d4ed8}.kpi-l{font-size:11px;color:#555555}@media print{body{padding:16px}}</style></head><body>
      <h1>Lemai Intelligence Platform — Executive Report</h1><p style="color:#555555;font-size:12px">Generated: ${now}</p>
      <h2>KPI Summary</h2><div>${kpiData.map(k=>`<div class="kpi"><div class="kpi-v">${k.value}</div><div class="kpi-l">${k.label}</div><div style="font-size:10px;color:#94a3b8">${k.trend}</div></div>`).join('')}</div>
      <h2>Active Alarms (${activeA.length})</h2>
      <table><tr><th>Type</th><th>Site</th><th>Location</th><th>Severity</th><th>Time</th><th>Status</th></tr>
      ${activeA.slice(0,20).map((a:any)=>`<tr><td>${a.type||'—'}</td><td>${a.site||'—'}</td><td>${a.location||'—'}</td><td>${a.severity||'—'}</td><td>${(a.timestamp||'').slice(11,16)||'—'}</td><td>${a.acknowledged?'ACK':'Active'}</td></tr>`).join('')}</table>
      <h2>Open Jobs (${jobs.filter((j:any)=>j.status!=='closed').length})</h2>
      <table><tr><th>Number</th><th>Type</th><th>Site</th><th>Priority</th><th>Status</th></tr>
      ${jobs.filter((j:any)=>j.status!=='closed').slice(0,20).map((j:any)=>`<tr><td>${j.number||j.id}</td><td>${j.type||'—'}</td><td>${j.site||'—'}</td><td>${j.priority||'—'}</td><td>${j.status||'—'}</td></tr>`).join('')}</table>
      </body></html>`);
      w.document.close();
      setTimeout(() => { w.print(); }, 400);
    }
    setTimeout(() => { setExporting(false); setExported(true); setTimeout(() => setExported(false), 2500); toast('Executive report opened for print/PDF', 'success'); }, 600);
  }

  const tabStyle = (t: string): React.CSSProperties => ({
    padding:'5px 14px', border:'none', background: period===t ? 'rgba(59,130,246,0.15)' : 'transparent',
    borderRadius:'7px', cursor:'pointer', fontSize:'11px', fontWeight:600,
    color: period===t ? '#3b82f6' : '#555555', transition:'all 0.15s',
  });

  const criticalSites = siteData.filter(s => s.risk === 'high').length;

  return (
    <Layout title="Executive Dashboard" subtitle="Multi-site KPI overview — live data">

      {/* Status strip */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', marginBottom:'20px', background: loading ? 'rgba(59,130,246,0.04)' : criticalSites > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(34,197,94,0.05)', border:`1px solid ${loading?'rgba(59,130,246,0.15)':criticalSites>0?'rgba(239,68,68,0.2)':'rgba(34,197,94,0.2)'}`, borderRadius:'4px', fontSize:'11px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'5px', fontWeight:700 }}>
          <span style={{ width:'7px', height:'7px', borderRadius:'50%', background: loading?'#3b82f6':criticalSites>0?'#ef4444':'#22c55e', display:'inline-block', animation: loading?'pulse 1s infinite':undefined, boxShadow:`0 0 6px ${loading?'#3b82f660':criticalSites>0?'#ef444460':'#22c55e60'}` }} />
          <span style={{ color: loading?'#3b82f6':criticalSites>0?'#ef4444':'#22c55e' }}>
            {loading ? 'LOADING LIVE DATA…' : criticalSites>0 ? 'SITES AT RISK' : 'ALL SITES NORMAL'}
          </span>
        </div>
        {!loading && <><span style={{ color:'rgba(255,255,255,0.08)' }}>|</span>
        <span style={{ color:'#555555' }}>{siteData.length} sites · {siteData.filter(s=>s.alarms>0).length} with active alarms · {armorAlarms.filter((a:any)=>!a.closedAt).length} total alarms</span></>}
        <span style={{ marginLeft:'auto', color:'#444444', fontSize:'10px', fontFamily:'monospace' }}>
          {new Date().toLocaleTimeString('en-AU',{timeZone:'Australia/Sydney',hour:'2-digit',minute:'2-digit',hour12:false})} AEST
        </span>
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <div style={{ display:'flex', gap:'4px', background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'4px' }}>
          {(['today','week','month'] as const).map(t => (
            <button key={t} onClick={() => setPeriod(t)} style={{ padding:'5px 14px', border:'none', background:period===t?'rgba(59,130,246,0.15)':'transparent', borderRadius:'7px', cursor:'pointer', fontSize:'11px', fontWeight:600, color:period===t?'#3b82f6':'#555555' }}>
              {t === 'today' ? 'Today' : t === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>
        <button onClick={handleExport} style={{ padding:'7px 18px', borderRadius:'4px', border:'none', fontSize:'11px', fontWeight:700, cursor:'pointer', background: exported ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: exported ? '#22c55e' : 'white', transition:'all 0.2s' }}>
          {exported ? '✓ PDF Opened' : exporting ? 'Generating…' : '↓ Export PDF Report'}
        </button>
      </div>

      {/* Top stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
        {loading ? Array(4).fill(0).map((_,i) => <SkeletonCard key={i} h={100} />) :
          topStats.map(s => (
            <div key={s.l} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px', position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, right:0, width:'60px', height:'60px', background:`${s.color}06`, borderRadius:'0 12px 0 50%' }} />
              <div style={{ fontSize:'11px', color:'#555555', fontWeight:600, marginBottom:'8px' }}>{s.l}</div>
              <div style={{ fontSize:'30px', fontWeight:800, color:s.color, lineHeight:'1', marginBottom:'4px' }}>{s.v}</div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:'8px' }}>
                <span style={{ fontSize:'11px', color: s.good ? '#22c55e' : '#f59e0b', fontWeight:700 }}>{s.arrow} {s.delta}</span>
                <Sparkline data={s.spark} color={s.color} />
              </div>
            </div>
          ))
        }
      </div>

      {/* KPI + site grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'20px' }}>
        {/* SLA KPIs */}
        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:'13px', fontWeight:700, color:'#e5e5e5' }}>SLA Performance</h3>
          {loading ? <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>{Array(4).fill(0).map((_,i) => <SkeletonRow key={i} height={70} />)}</div> :
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
              {kpiData.map(k => (
                <div key={k.label} style={{ display:'flex', gap:'12px', alignItems:'center' }}>
                  <Ring pct={k.pct} color={k.color} />
                  <div>
                    <div style={{ fontSize:'11px', fontWeight:600, color:'#e5e5e5', marginBottom:'2px' }}>{k.label}</div>
                    <div style={{ fontSize:'18px', fontWeight:800, color:k.color }}>{k.value}</div>
                    <div style={{ fontSize:'9px', color:'#555555', marginTop:'1px' }}>{k.sub}</div>
                    <div style={{ fontSize:'9px', color:'#555555', marginTop:'2px' }}>{k.trend}</div>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>

        {/* Site matrix */}
        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
          <h3 style={{ margin:'0 0 14px', fontSize:'13px', fontWeight:700, color:'#e5e5e5' }}>Site Status Matrix</h3>
          {loading ? <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>{Array(4).fill(0).map((_,i) => <SkeletonRow key={i} height={52} />)}</div> :
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {siteData.map(s => {
                const riskPct = s.risk==='high'?80:s.risk==='medium'?45:15;
                return (
                  <div key={s.site} style={{ padding:'9px 12px', borderRadius:'4px', background:'#0d0d0d', border:`1px solid ${RISK_COLOR[s.risk]}14` }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'6px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <div style={{ width:'7px', height:'7px', borderRadius:'50%', background:s.status==='Normal'?'#22c55e':s.status==='Alert'?'#ef4444':'#f59e0b', flexShrink:0 }} />
                        <span style={{ fontSize:'12px', fontWeight:600, color:'#e5e5e5' }}>{s.site}</span>
                      </div>
                      <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                        {s.alarms > 0 && <span style={{ fontSize:'10px', fontWeight:700, color:'#ef4444', background:'rgba(239,68,68,0.1)', padding:'1px 6px', borderRadius:'4px' }}>{s.alarms} alarms</span>}
                        {s.jobs > 0 && <span style={{ fontSize:'10px', color:'#f59e0b', background:'rgba(245,158,11,0.1)', padding:'1px 6px', borderRadius:'4px' }}>{s.jobs} jobs</span>}
                        <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 7px', borderRadius:'5px', background:`${RISK_COLOR[s.risk]}15`, color:RISK_COLOR[s.risk] }}>{s.risk.toUpperCase()}</span>
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div style={{ flex:1, height:'3px', background:'rgba(255,255,255,0.06)', borderRadius:'2px', overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${riskPct}%`, background:RISK_COLOR[s.risk], borderRadius:'2px', transition:'width 0.6s ease' }} />
                      </div>
                      <span style={{ fontSize:'9px', color:'#555555', whiteSpace:'nowrap' as const }}>Cam: {s.cameras}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          }
        </div>
      </div>

      {/* Weekly trend */}
      <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px', marginBottom:'20px' }}>
        <h3 style={{ margin:'0 0 16px', fontSize:'13px', fontWeight:700, color:'#e5e5e5' }}>7-Day Trend — Alarms vs Jobs</h3>
        {loading ? <SkeletonCard h={100} /> :
          <>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'8px', alignItems:'flex-end', height:'100px' }}>
              {weeklyAlarms.map((v, i) => {
                const maxA = Math.max(...weeklyAlarms, 1);
                const maxJ = Math.max(...weeklyJobs, 1);
                return (
                  <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', height:'100%', justifyContent:'flex-end' }}>
                    <div style={{ display:'flex', gap:'2px', alignItems:'flex-end', height:'80px' }}>
                      <div style={{ width:'12px', background:'rgba(239,68,68,0.6)', borderRadius:'2px 2px 0 0', height:`${Math.max(2, Math.round((v/maxA)*80))}px`, transition:'height 0.4s' }} title={`Alarms: ${v}`} />
                      <div style={{ width:'12px', background:'rgba(245,158,11,0.6)', borderRadius:'2px 2px 0 0', height:`${Math.max(2, Math.round((weeklyJobs[i]/maxJ)*80))}px`, transition:'height 0.4s' }} title={`Jobs: ${weeklyJobs[i]}`} />
                    </div>
                    <div style={{ fontSize:'9px', color:'#555555' }}>{DAYS[i]}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:'16px', marginTop:'10px', justifyContent:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'10px', color:'#555555' }}><div style={{ width:'10px', height:'10px', background:'rgba(239,68,68,0.6)', borderRadius:'2px' }} /> Alarms</div>
              <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'10px', color:'#555555' }}><div style={{ width:'10px', height:'10px', background:'rgba(245,158,11,0.6)', borderRadius:'2px' }} /> Jobs</div>
            </div>
          </>
        }
      </div>

      {/* Bottom row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
        {/* Critical alarms */}
        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
          <h3 style={{ margin:'0 0 12px', fontSize:'13px', fontWeight:700, color:'#e5e5e5' }}>Critical & High Severity Active</h3>
          {loading ? <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>{Array(3).fill(0).map((_,i) => <SkeletonRow key={i} height={48} />)}</div> :
            criticalData.length === 0
              ? <div style={{ padding:'24px', textAlign:'center', color:'#444444', fontSize:'12px' }}>No critical or high alarms — all clear</div>
              : <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                  {criticalData.map((j, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 10px', borderRadius:'4px', background:'rgba(239,68,68,0.04)', border:'1px solid rgba(239,68,68,0.1)' }}>
                      <div style={{ fontSize:'10px', color:'#555555', fontFamily:'monospace', flexShrink:0 }}>{j.time}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'11px', fontWeight:600, color:'#e5e5e5', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{j.type}</div>
                        <div style={{ fontSize:'10px', color:'#555555' }}>{j.site}</div>
                      </div>
                      <div style={{ fontSize:'9px', fontWeight:700, padding:'2px 5px', borderRadius:'5px', background: j.status==='acknowledged'?'rgba(34,197,94,0.1)':'rgba(245,158,11,0.1)', color: j.status==='acknowledged'?'#22c55e':'#f59e0b', flexShrink:0 }}>
                        {j.status}
                      </div>
                    </div>
                  ))}
                </div>
          }
        </div>

        {/* Live glance */}
        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
          <h3 style={{ margin:'0 0 12px', fontSize:'13px', fontWeight:700, color:'#e5e5e5' }}>Today at a Glance</h3>
          {loading ? <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>{Array(6).fill(0).map((_,i) => <SkeletonRow key={i} height={18} />)}</div> :
            <>
              {[
                { l:'Active alarms',        v:String(armorAlarms.filter((a:any)=>!a.closedAt).length),           c:armorAlarms.filter((a:any)=>!a.closedAt).length>0?'#ef4444':'#22c55e' },
                { l:'Alarms closed today',  v:String(armorAlarms.filter((a:any)=>a.closedAt).length),            c:'#22c55e' },
                { l:'Open jobs',            v:String(jobs.filter((j:any)=>j.status!=='closed').length),           c:jobs.filter((j:any)=>j.status!=='closed').length>5?'#f59e0b':'#e2e8f0' },
                { l:'Cameras online',       v:healthData?.cameras?.total>0?`${healthData.cameras.online}/${healthData.cameras.total}`:'—', c:'#22c55e' },
                { l:'Sites monitored',      v:String(siteData.length),                                            c:'#3b82f6' },
                { l:'Sites at risk',        v:String(criticalSites),                                              c:criticalSites>0?'#ef4444':'#22c55e' },
              ].map(item => (
                <div key={item.l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize:'11px', color:'#555555' }}>{item.l}</span>
                  <span style={{ fontSize:'11px', fontWeight:700, color:item.c }}>{item.v}</span>
                </div>
              ))}
              <div style={{ marginTop:'12px', padding:'10px', background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.12)', borderRadius:'4px' }}>
                <div style={{ fontSize:'10px', color:'#3b82f6', fontWeight:600, marginBottom:'4px' }}>Live Data</div>
                <div style={{ fontSize:'11px', color:'#555555', lineHeight:'1.5' }}>
                  All figures computed from live ArmorIP alarm feed and job management system. Refreshes every 2 minutes.
                </div>
              </div>
            </>
          }
        </div>
      </div>
    </Layout>
  );
}
